/**
 * Constants for annotation functionality
 * Centralized default values to ensure consistency across components
 */

export const ANNOTATION_DEFAULTS = {
  // Default normal vector (up direction)
  NORMAL: '0 1 0',
  
  // Default orbit for ModelViewer (camera position when adding annotation)
  MODEL_VIEWER_ORBIT: '45deg 65deg 0.6m',

  // Default orbit for AnnotationEditor (camera position when editing annotation)
  EDITOR_ORBIT: '35deg 70deg 0.75m',
  
  // Default orbit values (for calculateAnnotationOrbit function)
  DEFAULT_THETA: 45, // Horizontal angle in degrees
  DEFAULT_PHI: 65, // Vertical angle in degrees
  DEFAULT_RADIUS: 0.6, // Camera distance in meters
  
  // Min/Max values for smart defaults (in radians for phi, meters for radius)
  // Wider ranges to preserve more user angles
  MIN_PHI_RADIANS: 0.17, // ~10 degrees - very low angle
  MAX_PHI_RADIANS: 1.57, // ~90 degrees - very high angle (almost top view)
  MIN_RADIUS: 0.05, // Absolute minimum - only clamp values below this (very extreme close-up)
  MAX_RADIUS: 3.0, // Very far
  
  // Default field of view when viewing annotation
  FIELD_OF_VIEW: '32deg',
  
  // Drag threshold for distinguishing clicks from drags (in pixels)
  DRAG_THRESHOLD: 5,
  
  // Delay before processing annotation click (ms)
  CLICK_PROCESSING_DELAY: 100,

  /**
   * Must stay under server MAX_ANNOTATION_IMAGE_CHARS (4 MiB data-URL).
   * File bytes grow ~4/3 in base64, so 3 MB file ≈ 4 MB stored string.
   */
  MAX_IMAGE_FILE_BYTES: 3 * 1024 * 1024,
  MAX_IMAGE_DATA_URL_CHARS: 4 * 1024 * 1024,
} as const;

/**
 * Bootstrap camera orbit / FOV limits on `<model-viewer>` before the GLB is ready.
 *
 * `useModelViewerDynamicZoom` replaces these with bounding-box–scaled limits via
 * `applyDynamicModelViewerZoomLimits` once `getDimensions()` is available.
 */
export const MODEL_VIEWER_RELAXED_ZOOM_ATTRS = {
  /** Closest allowed orbit radius before dynamic scaling (metres). */
  'min-camera-orbit': 'auto auto 0.06m',
  /** Furthest allowed orbit radius before dynamic scaling (metres). */
  'max-camera-orbit': 'auto auto 20m',
  /** Narrowest field of view — more zoomed-in framing. */
  'min-field-of-view': '5deg',
  /** Widest field of view — more zoomed-out framing. */
  'max-field-of-view': '75deg',
} as const;
